// src/components/business/BusinessDiningSection.tsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import { supabase } from "../../supabaseClient";
import { 
  Loader2, Plus, Trash2, Save, Coffee, CheckCircle, AlertTriangle, 
  Camera, FileText, Link as LinkIcon, DollarSign, MapPin, Tag, CalendarCheck, Upload 
} from "lucide-react"; // ✅ "Upload" a été ajouté ici !

// Type mis à jour avec les 6 nouvelles fonctionnalités
type DiningRestaurant = {
  name?: string;
  serves?: string[];
  cuisine_type?: string;
  service_style?: string;
  opening_hours?: string;
  notes?: string;
  
  // NOUVEAUX CHAMPS
  image_url?: string;
  menu_url?: string;
  dietary_tags?: string[];
  price_range?: string;
  dress_code?: string;
  location_in_hotel?: string;
  reservation_link?: string;
};

type DiningData = {
  has_restaurant: boolean;
  restaurants: DiningRestaurant[];
};

type Props = {
  businessId: string | null | undefined;
};

const SERVES_OPTIONS = ["Déjeuner", "Dîner", "Souper", "Brunch", "Café/Collations"];
const SERVICE_STYLE_OPTIONS = ["À la carte", "Buffet", "Service aux chambres", "Bar", "Take-out"];
const DIETARY_OPTIONS = ["Végétarien", "Végan", "Sans Gluten", "Halal", "Casher", "Sans Arachides"];
const PRICE_RANGES = ["$", "$$", "$$$", "$$$$"];
const DRESS_CODES = ["Décontracté", "Chic décontracté", "Élégant", "Tenue de soirée", "Tenue de plage"];

const emptyRestaurant = (): DiningRestaurant => ({
  name: "",
  serves: [],
  cuisine_type: "",
  service_style: "",
  opening_hours: "",
  notes: "",
  image_url: "",
  menu_url: "",
  dietary_tags: [],
  price_range: "$$",
  dress_code: "",
  location_in_hotel: "",
  reservation_link: ""
});

export default function BusinessDiningSection({ businessId }: Props) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tableMissing, setTableMissing] = useState(false);
  const [uploadingState, setUploadingState] = useState<{ idx: number, type: 'image' | 'menu' } | null>(null);

  const [hasRestaurant, setHasRestaurant] = useState(false);
  const [restaurants, setRestaurants] = useState<DiningRestaurant[]>([]);

  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const canEdit = useMemo(() => !!businessId, [businessId]);

  const imageInputRef = useRef<HTMLInputElement>(null);
  const menuInputRef = useRef<HTMLInputElement>(null);
  const [activeUploadIdx, setActiveUploadIdx] = useState<number | null>(null);

  const resetMessages = () => {
    setOkMsg(null);
    setErrMsg(null);
  };

  const load = async () => {
    if (!businessId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setTableMissing(false);
    resetMessages();

    try {
      const res: any = await supabase
        .from("business_hotel_details")
        .select("dining")
        .eq("business_id", businessId)
        .maybeSingle();

      if (res?.status === 404) {
        setTableMissing(true);
        setHasRestaurant(false);
        setRestaurants([]);
        return;
      }

      if (res.error) throw res.error;

      const dining: DiningData | null = res.data?.dining ?? null;

      if (!dining) {
        setHasRestaurant(false);
        setRestaurants([]);
        return;
      }

      setHasRestaurant(!!dining.has_restaurant);
      setRestaurants(Array.isArray(dining.restaurants) ? dining.restaurants : []);
    } catch (e: any) {
      console.error("load dining error:", e);
      setErrMsg(e?.message || "Erreur de chargement.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  // Fonction générique pour gérer les tableaux (Plats & Restrictions)
  const toggleArrayField = (idx: number, field: 'serves' | 'dietary_tags', value: string) => {
    setRestaurants((prev) => {
      const next = [...prev];
      const r = { ...(next[idx] || {}) };
      const currentSet = new Set((r[field] || []) as string[]);
      if (currentSet.has(value)) currentSet.delete(value);
      else currentSet.add(value);
      r[field] = Array.from(currentSet);
      next[idx] = r;
      return next;
    });
  };

  const updateRestaurant = (idx: number, patch: Partial<DiningRestaurant>) => {
    setRestaurants((prev) => {
      const next = [...prev];
      next[idx] = { ...(next[idx] || {}), ...patch };
      return next;
    });
  };

  const addRestaurant = () => {
    resetMessages();
    setRestaurants((prev) => [...prev, emptyRestaurant()]);
    setHasRestaurant(true);
  };

  const removeRestaurant = (idx: number) => {
    resetMessages();
    setRestaurants((prev) => prev.filter((_, i) => i !== idx));
  };

  // GESTION DES UPLOADS (Photos & Menus PDF)
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: 'image' | 'menu') => {
    const file = e.target.files?.[0];
    if (!file || !businessId || activeUploadIdx === null) return;

    setUploadingState({ idx: activeUploadIdx, type });
    setErrMsg(null);
    try {
      const ext = file.name.split('.').pop();
      const fileName = `${businessId}/dining/${type}_${activeUploadIdx}_${Date.now()}.${ext}`;
      
      const { error } = await supabase.storage
        .from('business-media')
        .upload(fileName, file, { upsert: true });

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('business-media')
        .getPublicUrl(fileName);

      updateRestaurant(activeUploadIdx, type === 'image' ? { image_url: publicUrl } : { menu_url: publicUrl });
      setOkMsg(`Fichier ajouté avec succès !`);
    } catch (err: any) {
      setErrMsg(`Erreur d'upload : ${err.message}`);
    } finally {
      setUploadingState(null);
      setActiveUploadIdx(null);
      e.target.value = ''; 
    }
  };

  const triggerUpload = (idx: number, type: 'image' | 'menu') => {
    setActiveUploadIdx(idx);
    if (type === 'image') imageInputRef.current?.click();
    if (type === 'menu') menuInputRef.current?.click();
  };

  const save = async () => {
    resetMessages();
    if (!businessId) {
      setErrMsg("Business ID manquant.");
      return;
    }

    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setErrMsg("Connecte-toi pour modifier la restauration.");
        return;
      }

      const payload: DiningData = {
        has_restaurant: !!hasRestaurant,
        restaurants: (restaurants || []).map((r) => ({
          name: (r.name || "").trim(),
          serves: Array.isArray(r.serves) ? r.serves : [],
          cuisine_type: (r.cuisine_type || "").trim(),
          service_style: (r.service_style || "").trim(),
          opening_hours: (r.opening_hours || "").trim(),
          notes: (r.notes || "").trim(),
          image_url: r.image_url || "",
          menu_url: r.menu_url || "",
          dietary_tags: Array.isArray(r.dietary_tags) ? r.dietary_tags : [],
          price_range: r.price_range || "$$",
          dress_code: r.dress_code || "",
          location_in_hotel: r.location_in_hotel || "",
          reservation_link: r.reservation_link || ""
        })),
      };

      const upsertRes = await supabase
        .from("business_hotel_details")
        .upsert(
          { business_id: businessId, dining: payload },
          { onConflict: "business_id" }
        )
        .select("id")
        .maybeSingle();

      if ((upsertRes as any)?.status === 404) {
        setTableMissing(true);
        throw new Error("La table business_hotel_details n’existe pas (SQL non exécuté).");
      }

      if (upsertRes.error) throw upsertRes.error;

      setOkMsg("Restauration enregistrée ✅");
      await load();
    } catch (e: any) {
      console.error("save dining error:", e);
      setErrMsg(e?.message || "Erreur lors de l’enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  if (!canEdit) {
    return (
      <div className="bg-white border border-slate-100 rounded-[2.5rem] p-6 md:p-8 shadow-sm">
        <div className="flex items-center gap-2 mb-2">
          <Coffee className="text-slate-900" />
          <h2 className="text-xl font-black text-slate-900">Restauration</h2>
        </div>
        <p className="text-sm text-slate-600">
          Impossible de charger la restauration : <b>businessId manquant</b>.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-100 rounded-[2.5rem] p-6 md:p-8 shadow-sm relative overflow-hidden" data-forfeo-ui-v218="dining" data-commerce-panel-v219="dining" data-commerce-v220-panel="dining" data-commerce-v221-panel="dining">
      {/* Inputs cachés pour les uploads */}
      <input type="file" ref={imageInputRef} onChange={e => handleFileUpload(e, 'image')} accept="image/*" className="hidden" />
      <input type="file" ref={menuInputRef} onChange={e => handleFileUpload(e, 'menu')} accept="application/pdf,image/*" className="hidden" />

      <div className="flex items-start justify-between gap-3 relative z-10">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 bg-blue-50 text-blue-600 rounded-2xl"><Coffee size={24} /></div>
            <h2 className="text-2xl font-black text-slate-900">Restauration & Bars</h2>
          </div>
          <p className="text-sm text-slate-700 font-medium">
            Gérez vos restaurants, menus, photos et liens de réservation directement sur votre fiche.
          </p>
        </div>

        <button
          type="button"
          onClick={addRestaurant}
          className="shrink-0 bg-blue-600 text-white border border-blue-600 px-5 py-3 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-blue-700 transition flex items-center gap-2 shadow-lg"
        >
          <Plus size={16} /> Ajouter
        </button>
      </div>

      {loading ? (
        <div className="mt-8 flex items-center gap-3 text-blue-600 font-bold justify-center py-10 bg-slate-50 rounded-3xl">
          <Loader2 className="animate-spin" size={24} /> Chargement des données...
        </div>
      ) : (
        <div className="relative z-10">
          {tableMissing && (
            <div className="mt-6 bg-yellow-50 border border-yellow-100 rounded-2xl p-4 text-sm text-yellow-900">
              <b>SQL manquant :</b> la table <code>business_hotel_details</code> n’existe pas encore.
            </div>
          )}

          <div className="mt-8 flex items-center justify-between gap-3 bg-slate-50 border border-slate-100 rounded-2xl p-4 shadow-sm">
            <div>
              <div className="font-black text-slate-900">Restaurant(s) sur place</div>
              <div className="text-xs text-slate-700 font-bold">
                Si désactivé, la section sera masquée aux voyageurs.
              </div>
            </div>

            <label className="inline-flex items-center cursor-pointer select-none">
              <span className="sr-only">Activer Restauration</span>
              <div className={`w-12 h-6 rounded-full relative transition-colors duration-300 ${hasRestaurant ? 'bg-green-500' : 'bg-slate-300'}`}>
                <span className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-all duration-300 shadow-sm ${hasRestaurant ? 'translate-x-6' : ''}`} />
              </div>
              <input type="checkbox" className="sr-only" checked={hasRestaurant} onChange={() => { resetMessages(); setHasRestaurant((v) => !v); }} />
            </label>
          </div>

          {hasRestaurant && restaurants.length === 0 && (
            <div className="mt-6 text-center py-12 border-2 border-dashed border-slate-200 rounded-[2rem] text-slate-700">
              <Coffee className="mx-auto mb-3 opacity-20" size={48} />
              <p className="font-bold">Aucun restaurant configuré.</p>
              <p className="text-xs mt-1">Cliquez sur le bouton "Ajouter" en haut à droite.</p>
            </div>
          )}

          {hasRestaurant && restaurants.length > 0 && (
            <div className="mt-8 space-y-8">
              {restaurants.map((r, idx) => (
                <div key={idx} className="border border-slate-200 bg-white rounded-[2rem] p-6 shadow-sm hover:shadow-md transition-shadow relative">
                  
                  {/* Bouton de suppression */}
                  <button
                    type="button"
                    onClick={() => removeRestaurant(idx)}
                    className="absolute top-6 right-6 bg-red-50 text-red-500 p-2 rounded-xl hover:bg-red-500 hover:text-white transition group"
                    title="Supprimer ce restaurant"
                  >
                    <Trash2 size={18} className="group-hover:scale-110 transition-transform" />
                  </button>

                  <h3 className="text-lg font-black text-slate-900 mb-6 flex items-center gap-2 border-b border-slate-100 pb-4">
                    Restaurant #{idx + 1}
                  </h3>

                  <div className="flex flex-col lg:flex-row gap-8">
                    {/* SECTION PHOTO (Gauche) */}
                    <div className="w-full lg:w-1/4">
                      <label className="block text-[10px] font-black uppercase tracking-widest text-slate-700 mb-2">
                        Photo principale
                      </label>
                      <div 
                        onClick={() => triggerUpload(idx, 'image')}
                        className="aspect-square bg-slate-50 border-2 border-dashed border-slate-300 rounded-3xl flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition overflow-hidden group relative"
                      >
                        {r.image_url ? (
                          <>
                            <img src={r.image_url} alt="Restaurant" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                              <Camera className="text-white" size={32} />
                            </div>
                          </>
                        ) : (
                          <>
                            {uploadingState?.idx === idx && uploadingState.type === 'image' ? (
                              <Loader2 className="animate-spin text-blue-500 mb-2" size={32} />
                            ) : (
                              <Camera className="text-slate-700 mb-2 group-hover:text-blue-500 transition group-hover:scale-110" size={32} />
                            )}
                            <span className="text-xs font-bold text-slate-700 group-hover:text-blue-600">Ajouter une photo</span>
                          </>
                        )}
                      </div>
                    </div>

                    {/* INFOS PRINCIPALES (Droite) */}
                    <div className="flex-1 space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-700 mb-2">Nom du restaurant / Bar</label>
                          <input
                            value={r.name || ""}
                            onChange={(e) => updateRestaurant(idx, { name: e.target.value })}
                            className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 font-bold text-slate-900 outline-none focus:ring-2 ring-blue-500 focus:bg-white transition"
                            placeholder="Ex: Le Bistro Forfeo"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-700 mb-2 flex items-center gap-1"><MapPin size={12}/> Emplacement exact</label>
                          <input
                            value={r.location_in_hotel || ""}
                            onChange={(e) => updateRestaurant(idx, { location_in_hotel: e.target.value })}
                            className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 font-bold text-slate-900 outline-none focus:ring-2 ring-blue-500 focus:bg-white transition"
                            placeholder="Ex: Sur le toit (Rooftop), Lobby..."
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-700 mb-2">Cuisine</label>
                          <input
                            value={r.cuisine_type || ""}
                            onChange={(e) => updateRestaurant(idx, { cuisine_type: e.target.value })}
                            className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 font-bold text-slate-900 outline-none focus:ring-2 ring-blue-500 focus:bg-white transition"
                            placeholder="Ex: Italienne, Fruits de mer…"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-700 mb-2">Service</label>
                          <select
                            value={r.service_style || ""}
                            onChange={(e) => updateRestaurant(idx, { service_style: e.target.value })}
                            className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 font-bold text-slate-900 outline-none focus:ring-2 ring-blue-500 focus:bg-white transition"
                          >
                            <option value="">—</option>
                            {SERVICE_STYLE_OPTIONS.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-700 mb-2 flex items-center gap-1"><DollarSign size={12}/> Prix</label>
                          <div className="flex bg-slate-50 rounded-2xl p-1 border border-slate-200">
                            {PRICE_RANGES.map(price => (
                              <button
                                key={price}
                                type="button"
                                onClick={() => updateRestaurant(idx, { price_range: price })}
                                className={`flex-1 py-2 text-sm font-black rounded-xl transition ${r.price_range === price ? 'bg-white shadow-sm text-green-600' : 'text-slate-700 hover:text-slate-600'}`}
                              >
                                {price}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-700 mb-2 flex items-center gap-1">Horaires</label>
                          <input
                            value={r.opening_hours || ""}
                            onChange={(e) => updateRestaurant(idx, { opening_hours: e.target.value })}
                            className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 font-bold text-slate-900 outline-none focus:ring-2 ring-blue-500 focus:bg-white transition"
                            placeholder="Ex: 7h–10h / 17h–22h"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-black uppercase tracking-widest text-slate-700 mb-2">Code Vestimentaire (Dress Code)</label>
                          <select
                            value={r.dress_code || ""}
                            onChange={(e) => updateRestaurant(idx, { dress_code: e.target.value })}
                            className="w-full bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 font-bold text-slate-900 outline-none focus:ring-2 ring-blue-500 focus:bg-white transition"
                          >
                            <option value="">Aucun spécifique</option>
                            {DRESS_CODES.map((code) => <option key={code} value={code}>{code}</option>)}
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* SECTION TAGS (Repas & Restrictions) */}
                  <div className="mt-8 pt-6 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-8" data-forfeo-v218-dining-tags>
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-slate-700 mb-3 flex items-center gap-2"><Coffee size={14}/> Repas Servis</label>
                      <div className="flex flex-wrap gap-2">
                        {SERVES_OPTIONS.map((opt) => {
                          const active = (r.serves || []).includes(opt);
                          return (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => toggleArrayField(idx, 'serves', opt)}
                              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition border ${
                                active ? "bg-blue-600 text-white border-blue-600 shadow-md" : "bg-white text-slate-700 border-slate-300 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-700"
                              }`}
                            >
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-slate-700 mb-3 flex items-center gap-2"><Tag size={14}/> Options Spéciales (Diet)</label>
                      <div className="flex flex-wrap gap-2">
                        {DIETARY_OPTIONS.map((opt) => {
                          const active = (r.dietary_tags || []).includes(opt);
                          return (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => toggleArrayField(idx, 'dietary_tags', opt)}
                              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-widest transition border ${
                                active ? "bg-emerald-600 text-white border-emerald-600 shadow-md" : "bg-white text-slate-700 border-slate-300 hover:bg-emerald-50 hover:border-emerald-400 hover:text-emerald-700"
                              }`}
                            >
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* LIENS & MENUS */}
                  <div className="mt-8 pt-6 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-slate-700 mb-2 flex items-center gap-2"><FileText size={14}/> Menu du restaurant</label>
                      <div className="flex gap-2">
                        <input
                          value={r.menu_url || ""}
                          onChange={(e) => updateRestaurant(idx, { menu_url: e.target.value })}
                          className="flex-1 bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 text-sm font-medium text-slate-900 outline-none focus:ring-2 ring-blue-500"
                          placeholder="Lien URL (ex: mon-site.com/menu) ou uploadez un PDF"
                        />
                        <button
                          type="button"
                          onClick={() => triggerUpload(idx, 'menu')}
                          className="px-4 bg-slate-100 text-slate-600 rounded-2xl hover:bg-slate-200 transition font-bold text-xs uppercase flex flex-col items-center justify-center gap-1"
                        >
                          {uploadingState?.idx === idx && uploadingState.type === 'menu' ? <Loader2 className="animate-spin" size={16} /> : <Upload size={16} />}
                          PDF
                        </button>
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10px] font-black uppercase tracking-widest text-slate-700 mb-2 flex items-center gap-2"><CalendarCheck size={14}/> Lien de réservation (Table)</label>
                      <div className="relative">
                        <LinkIcon className="absolute left-4 top-3.5 text-slate-700" size={16} />
                        <input
                          value={r.reservation_link || ""}
                          onChange={(e) => updateRestaurant(idx, { reservation_link: e.target.value })}
                          className="w-full pl-11 bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 text-sm font-medium text-slate-900 outline-none focus:ring-2 ring-blue-500"
                          placeholder="Lien OpenTable, Zenchef, TheFork..."
                        />
                      </div>
                    </div>
                  </div>

                  {/* NOTES */}
                  <div className="mt-6">
                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-700 mb-2">
                      Informations complémentaires
                    </label>
                    <textarea
                      value={r.notes || ""}
                      onChange={(e) => updateRestaurant(idx, { notes: e.target.value })}
                      className="w-full min-h-[80px] bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3 font-medium text-sm text-slate-900 outline-none focus:ring-2 ring-blue-500 resize-none focus:bg-white transition"
                      placeholder="Ex: Animaux non acceptés, vue panoramique sur l'océan..."
                    />
                  </div>

                </div>
              ))}
            </div>
          )}

          {/* Messages de retour */}
          {okMsg && (
            <div className="mt-6 bg-green-50 border-2 border-green-200 text-green-800 rounded-2xl p-4 text-sm font-black flex items-center gap-3 shadow-sm animate-in zoom-in duration-300">
              <CheckCircle size={20} className="text-green-500" /> {okMsg}
            </div>
          )}
          {errMsg && (
            <div className="mt-6 bg-red-50 border-2 border-red-200 text-red-800 rounded-2xl p-4 text-sm font-black flex items-center gap-3 shadow-sm animate-in zoom-in duration-300">
              <AlertTriangle size={20} className="text-red-500" /> {errMsg}
            </div>
          )}

          {/* Bouton de sauvegarde global */}
          <div className="mt-8 flex flex-col md:flex-row gap-4 md:items-center md:justify-between border-t border-slate-100 pt-6">
            <div className="text-xs text-slate-700 font-bold bg-slate-50 px-4 py-2 rounded-xl">
              💡 N'oubliez pas de sauvegarder vos modifications.
            </div>

            <button
              type="button"
              onClick={save}
              disabled={saving || tableMissing}
              className="bg-blue-600 text-white px-8 py-4 rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-blue-700 transition shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {saving ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
              {saving ? "Enregistrement en cours..." : "Sauvegarder les restaurants"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
